/**
 * The attitude system: the torso as one rotating body (v1 plan section 6.1).
 * `phi` is the orientation (positive pitches the head toward +x) and `phiDot`
 * its rate. Tilt applies torque, the tuck and the reach set the moment of
 * inertia (and changing it conserves angular momentum), drag slows the spin,
 * and a righting spring pulls toward upright: strong on the ground, weak in
 * the air. On a bar the body hangs along the arm line plus a pump lean, and a
 * release carries part of that rotation into the flight.
 */

import { abs, clamp, moveToward, sin, TWO_PI, wrapAngle } from "./fastmath";
import type { SimState } from "./state";
import type { CompiledTuning } from "./tuning";
import { DT, type InputFrame } from "./types";

/** Moment of inertia for a tuck and reach amount. */
export function inertia(t: CompiledTuning, tuck: number, reach: number): number {
  return t.inertiaBase * (1 - (1 - t.inertiaTucked) * tuck) * (1 + (t.inertiaArmsOut - 1) * reach);
}

/**
 * Recompute the inertia from the current tuck and reach. Angular momentum is
 * conserved across the change, so tucking spins faster and reaching slows the spin.
 */
export function updateInertia(s: SimState, t: CompiledTuning): void {
  let I = inertia(t, s.tuck, s.reach);
  if (I < 1e-6) I = 1e-6;
  if (s.inertia > 0 && I !== s.inertia) s.phiDot *= s.inertia / I;
  s.inertia = I;
}

/**
 * One tick of free rotation off a bar: torque from the tilt, drag, and the
 * righting spring for the context (ground or air). Semi-implicit Euler.
 */
export function integrateAttitude(
  s: SimState,
  input: InputFrame,
  t: CompiledTuning,
  grounded: boolean,
): void {
  const I = s.inertia;
  const righting = grounded ? t.rightingGround : t.rightingAir;
  const drag = grounded ? t.spinDragGround : t.spinDrag;
  const acc = (t.tiltTorque * input.tiltX - righting * sin(s.phi)) / I - drag * s.phiDot;
  s.phiDot += acc * DT;
  s.phi = wrapAngle(s.phi + s.phiDot * DT);
}

/**
 * While attached the body hangs along the arm line (`-theta`) plus the pump
 * lean, eased onto it from the flight attitude over the attach ticks. `phiDot`
 * is the kinematic rate, so a release starts from it.
 */
export function pinAttitudeToBar(s: SimState, input: InputFrame, t: CompiledTuning): void {
  const before = s.phi;
  s.barLean = moveToward(s.barLean, clamp(input.tiltX, -1, 1), t.barLeanRate * DT);
  const onLine = -s.theta + s.barLean * t.barLeanClampRad;
  let target = onLine;
  if (s.attachLeft > 0 && s.attachTotal > 0) {
    // Smoothstep from the attitude at the grab onto the arm line, the short way round.
    const f = 1 - s.attachLeft / s.attachTotal;
    const e = f * f * (3 - 2 * f);
    target = s.attachPhi0 + wrapAngle(onLine - s.attachPhi0) * e;
  }
  s.phi = wrapAngle(target);
  s.phiDot = wrapAngle(s.phi - before) / DT;
}

/**
 * On a ledge the body hangs upright against the wall: `phi` eases from the attitude at the grab
 * onto 0 over the attach ticks (a smoothstep, the short way round) and stays there; `phiDot` is
 * the kinematic rate, so a drop or a mantle starts from it.
 */
export function pinAttitudeUpright(s: SimState): void {
  const before = s.phi;
  let target = 0;
  if (s.attachLeft > 0 && s.attachTotal > 0) {
    const f = 1 - s.attachLeft / s.attachTotal;
    const e = f * f * (3 - 2 * f);
    target = s.attachPhi0 + wrapAngle(-s.attachPhi0) * e;
  }
  s.phi = wrapAngle(target);
  s.phiDot = wrapAngle(s.phi - before) / DT;
}

/**
 * One tick of a roll: no righting and no stick, the air's drag, and the rolling contact: the spin
 * locks toward the speed over the rolling radius (the mass height plus rollRadius, so a curled body
 * turns faster than an extended one, and never slower than rollMinSpin so a slow roll still comes
 * round) and eases out over the last rollFinishDeg of the turn so the feet come under. Returns true
 * when the turn is complete.
 */
export function integrateRoll(s: SimState, t: CompiledTuning): boolean {
  const radius = t.standHeight * 0.5 - t.tuckComDrop * s.tuck + t.rollRadius;
  const rolling = radius > 1e-6 ? abs(s.vx) / radius : 0;
  const base = rolling > t.rollMinSpin ? rolling : t.rollMinSpin;
  const left = TWO_PI - s.rollTurn;
  const finish = t.rollFinishRad > 0 ? clamp(left / t.rollFinishRad, 0, 1) : 1;
  const target = s.rollDir * (t.rollMinSpin + (base - t.rollMinSpin) * finish);
  const k = t.rollGrip * DT;
  const acc = -t.spinDrag * s.phiDot + ((target - s.phiDot) * (k > 1 ? 1 : k)) / DT;
  const before = s.phi;
  s.phiDot += acc * DT;
  s.phi = wrapAngle(s.phi + s.phiDot * DT);
  s.rollTurn += wrapAngle(s.phi - before) * s.rollDir;
  return s.rollTurn >= TWO_PI;
}

/** A release keeps `phi` and carries `releaseSpin` of the body's rotation on the bar into the flight. */
export function releaseAttitude(s: SimState, t: CompiledTuning): void {
  s.phiDot *= t.releaseSpin;
  s.barLean = 0;
}

/**
 * A touchdown pitches the body in the direction of travel: the feet stop and
 * the mass keeps going. `dir` is the sign of the horizontal velocity (0 for a
 * straight drop, which pitches nothing).
 */
export function impactAttitude(s: SimState, t: CompiledTuning, impact: number, dir: number): void {
  const kick = dir * t.impactPitch * abs(impact);
  // Applied like the tick's other rate changes, before the position update, so phi moves by phiDot.
  s.phiDot += kick;
  s.phi = wrapAngle(s.phi + kick * DT);
}
